import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Upload, useGetApiV1UploadsByIdQuery } from '@wellesley/openapi'
import { useInViewPort } from 'hooks/useInViewPort'
import isUploadStable from '../../helpers/isUploadStable'
import { platformSettingsSlice } from '@wellesley/settings/slices/platformSettingsSlice'
import isNewUpload from '../../helpers/isNewUpload'

type HookProps = {
  upload: Upload
}

const interval = 5000

const useCheckUpload = ({ upload }: HookProps) => {
  const [isReady, setIsReady] = useState(false)
  const [currentUpload, setCurrentUpload] = useState<Upload>(upload)

  const containerRef = useRef<HTMLDivElement>(null)
  const { isVisible: isInView } = useInViewPort(containerRef)

  const videoExtensions = useSelector(
    platformSettingsSlice.selectors.selectVideoExtensions,
  )

  const isNewUploadFile = upload.files.some((file) =>
    isNewUpload(file.uri || ''),
  )

  useEffect(() => {
    upload && setCurrentUpload(upload)
  }, [upload.meta?.altText])

  const isProcessing = !isUploadStable({
    upload: currentUpload,
    videoExtensions,
  })

  const canPoll = isProcessing && isInView && !isReady && !isNewUploadFile

  const queryArg = useMemo(
    () => ({ id: currentUpload.uploadId }),
    [currentUpload.uploadId],
  )

  const { data } = useGetApiV1UploadsByIdQuery(queryArg, {
    skip: !canPoll,
    pollingInterval: canPoll ? interval : 0,
    refetchOnMountOrArgChange: true,
  })

  const isStable = useMemo(() => {
    if (!data) return false
    return isUploadStable({ upload: data, videoExtensions })
  }, [data, videoExtensions])

  useEffect(() => {
    if (data && isStable) {
      setCurrentUpload(data)
      setIsReady(true)
    }
  }, [data, isStable])

  return {
    containerRef,
    upload: currentUpload,
    isProcessing,
  }
}

export default useCheckUpload
